<template>
  <div
    class="font-medium text-[17px] leading-[18px] text-gray-800 pb-6 border-b border-[#DFDFDF]"
  >
    Preferences
  </div>

  <SettingsRow>
    <template #left><div class="my-auto">Default file privacy</div></template>

    <template #right>
      <div v-if="user" class="flex">
        <div class="mr-4">{{ capitalize(user.defaultFilePrivacy) }}</div>
        <div
          class="text-[14px] text-link"
          @click="emit('openDialog', 'DefaultFilePrivacy')"
        >
          Edit
        </div>
      </div>
      <Skeleton v-else class="w-[60px] h-[18px]" />
    </template>
  </SettingsRow>

  <SettingsRow>
    <template #left><div class="my-auto">Date format</div></template>

    <template #right>
      <div v-if="user" class="w-[200px]">
        <Select
          :model-value="dateFormat"
          :items="DATE_FORMATS"
          size="lg"
          @update:model-value="emit('updateDateFormat', $event)"
        />
      </div>
      <Skeleton v-else class="w-[200px] h-[38px]" />
    </template>
  </SettingsRow>

  <div class="mb-11 border-b border-[#DFDFDF] py-6">
    <SettingsRow class="border-none h-auto">
      <template #left><div class="my-auto">Automatic timezone</div></template>

      <template #right>
        <div v-if="user" class="flex items-center">
          <div v-if="automaticTimezone" class="mr-4">
            {{ currentTimezoneLabel }}
          </div>
          <Toggle
            :model-value="automaticTimezone"
            :size="isDesktopLayout ? 'md' : 'lg'"
            @update:model-value="emit('updateAutomaticTimezone', $event)"
          />
        </div>
        <Skeleton
          v-else
          :class="isDesktopLayout ? 'h-[20px] w-[34px]' : 'h-[30px] w-[49px]'"
        />
      </template>
    </SettingsRow>

    <div
      v-if="!automaticTimezone"
      class="mt-4 flex items-center justify-end gap-3"
    >
      <div class="w-1/2 mobile-l:w-[200px]">
        <Select
          :model-value="customTimezone.country"
          :items="countryOptions"
          size="lg"
          label="Country"
          @update:model-value="emit('updateCustomTimezoneCountry', $event)"
        />
      </div>
      <div class="w-1/2 mobile-l:w-[200px]">
        <Select
          :model-value="customTimezone.name"
          :items="timezoneOptions"
          size="lg"
          label="Timezone"
          @update:model-value="emit('updateCustomTimezoneName', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SettingsRow from '@/features/settings/components/SettingsRow.vue'
import { Select, Toggle, Skeleton } from '@dacr/library'
import { capitalize } from '@/shared/utils/string'
import { useDevice } from '@/shared/composables/useDevice'
import type {
  UserFieldsFragment,
  TimezoneInput,
} from '@/shared/graphql/legacy/generated'

const DATE_FORMATS = [
  { value: 'd/M/yyyy', label: 'DD/MM/YYYY' },
  { value: 'M/d/yyyy', label: 'MM/DD/YYYY' },
  { value: 'yyyy/M/d', label: 'YYYY/MM/DD' },
]

defineProps<{
  user: UserFieldsFragment | null
  dateFormat: string
  automaticTimezone: boolean
  currentTimezoneLabel?: string
  customTimezone: TimezoneInput
  countryOptions: { label: string; value: string }[]
  timezoneOptions: { label: string; value: string }[]
}>()

const emit = defineEmits<{
  (e: 'openDialog', name: string): void
  (e: 'updateDateFormat', value: string): void
  (e: 'updateAutomaticTimezone', value: boolean): void
  (e: 'updateCustomTimezoneCountry', value: string): void
  (e: 'updateCustomTimezoneName', value: string): void
}>()

const { isDesktopLayout } = useDevice()
</script>
